import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { 
  Star, 
  ChevronRight,
  Award,
  CheckCircle2,
  Sparkles
} from 'lucide-react';
import { ProviderLevelBadge } from './ServiceProviderBadges';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Reliable placeholder image
const PLACEHOLDER_IMAGE = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 100 100"%3E%3Crect fill="%23e5e7eb" width="100" height="100"/%3E%3Ctext fill="%239ca3af" font-family="sans-serif" font-size="40" x="50" y="60" text-anchor="middle"%3EP%3C/text%3E%3C/svg%3E';

/**
 * ServiceCardPremium (Grid Card B) - COMPACT VERSION
 * Premium service card with rating inline
 * Used for: Wedding Fittings, Custom Gowns, Bespoke Tailoring
 */
const ServiceCardPremium = ({ 
  service, 
  onBook, 
  onViewDetails,
  onRequestQuote,
  className = '' 
}) => {
  const {
    id,
    provider_name,
    provider_image,
    service_name,
    rating = 0,
    review_count = 0,
    starting_price,
    currency = 'Rs',
    verification_level = 0,
    achievements = [],
    is_sponsored = false,
    specializations = [],
    requires_quote = false,
  } = service;

  const imageUrl = provider_image 
    ? (provider_image.startsWith('http') ? provider_image : `${API_URL}${provider_image}`)
    : PLACEHOLDER_IMAGE;

  return (
    <Card 
      className={`group hover:shadow-lg transition-all duration-200 overflow-hidden border hover:border-purple-400 cursor-pointer ${className}`}
      onClick={() => onViewDetails?.(service)}
      data-testid={`service-card-premium-${id}`}
    >
      {/* Compact Premium Header */}
      <div className="bg-gradient-to-r from-purple-600 to-pink-500 text-white px-3 py-1.5 flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <Award className="h-3.5 w-3.5" />
          <span className="text-xs font-semibold">Premium Provider</span>
        </div>
        {is_sponsored && (
          <Badge className="bg-white/20 text-white border-0 text-xs px-1.5 py-0">
            <Sparkles className="h-3 w-3 mr-0.5" />
            Featured
          </Badge>
        )}
      </div>
      
      <CardContent className="p-3">
        {/* Provider Profile - Compact */}
        <div className="flex items-center gap-2.5 mb-2">
          <div className="relative flex-shrink-0">
            <img
              src={imageUrl}
              alt={provider_name}
              className="w-11 h-11 rounded-full object-cover border-2 border-purple-200"
              onError={(e) => {
                e.target.src = PLACEHOLDER_IMAGE;
              }}
            />
            {verification_level >= 3 && (
              <div className="absolute -bottom-0.5 -right-0.5 bg-purple-600 rounded-full p-0.5">
                <CheckCircle2 className="h-3 w-3 text-white" />
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-gray-900 truncate group-hover:text-purple-600 transition-colors text-sm">
              {provider_name}
            </h3>
            {/* Service name with rating inline */}
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500 truncate">{service_name}</span>
              <div className="flex items-center gap-0.5 flex-shrink-0">
                <Star className="h-3 w-3 text-yellow-500 fill-yellow-500" />
                <span className="text-xs font-semibold">{rating.toFixed(1)}</span>
                <span className="text-xs text-gray-400">({review_count})</span>
              </div>
            </div>
            <ProviderLevelBadge level={verification_level} size="xs" />
          </div>
        </div>
        
        {/* Specializations - Compact */}
        {specializations.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-2">
            {specializations.slice(0, 3).map((spec, index) => (
              <Badge key={index} variant="secondary" className="text-xs py-0 px-1.5 bg-purple-50 text-purple-700">
                {spec}
              </Badge>
            ))}
          </div>
        )}
        
        {/* Price & CTA - Compact */}
        <div className="flex items-center justify-between pt-2 border-t">
          <div>
            <p className="text-xs text-gray-400">Starting from</p>
            <p className="text-sm font-bold text-purple-600">
              {requires_quote ? 'Get Quote' : `${currency} ${starting_price?.toLocaleString()}`}
            </p>
          </div>
          
          <Button 
            size="sm"
            className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 h-8 text-xs"
            onClick={(e) => {
              e.stopPropagation();
              requires_quote ? onRequestQuote?.(service) : onBook?.(service);
            }}
            data-testid={`book-premium-${id}`}
          >
            {requires_quote ? 'Get Quote' : 'Book Now'}
            <ChevronRight className="h-3.5 w-3.5 ml-0.5" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default ServiceCardPremium;
